/**
 * useCurrentUserRole — Resolves the current user's platform role.
 *
 * In live mode, calls the backend endpoint GET /api/users/me/role
 * to fetch the authenticated user's assigned role for the active company.
 *
 * Falls back to mock data in demo/tour mode or if the API call fails.
 * The mock derives the role from a localStorage override key
 * `evo_debug_role` so developers can test different role views
 * without a backend. Valid values: 'administrator' | 'executive' |
 * 'project_manager' | 'project_team'.
 */

import { useState, useEffect, useMemo } from 'react';
import { getDataMode, fetchCurrentUserRole } from '@/lib/data-service';
import type { ReportPermissions, RolePermissions } from '@/types/roles.types';

// ── Role type ─────────────────────────────────────────────────────────────────

export type PlatformRole =
  | 'administrator'
  | 'executive'
  | 'project_manager'
  | 'project_team';

export interface CurrentUserRole {
  /** Resolved platform role for the active user + company. */
  role: PlatformRole;
  /**
   * The report/dashboard permissions for this role.
   * When populated, DashboardPersonaContext uses permission-driven resolution
   * instead of the legacy role-name mapping.
   * Will be populated from the real API once DB-BE-2 is implemented.
   */
  permissions: ReportPermissions | null;
  /**
   * True when this is a mock/fallback value.
   * Will be false once the backend endpoint is wired in.
   */
  isMock: boolean;
}

// ── Role → dashboard persona mapping (legacy fallback) ───────────────────────
// Used when permissions object is not yet available (before DB-BE-2 is wired).

export type RoleDashboardPersona = 'executive' | 'project_manager' | 'project_team';

export const ROLE_TO_DASHBOARD_PERSONA: Record<PlatformRole, RoleDashboardPersona> = {
  administrator:   'executive',       // Admin sees Executive Dashboard
  executive:       'executive',       // Executive sees Executive Dashboard
  project_manager: 'project_manager', // PM sees PM Dashboard
  project_team:    'project_team',    // Project Team sees Project List
};

// ── Mock permissions per platform role ───────────────────────────────────────
// These mirror the defaults in roles-mock-data.ts and are used by
// DashboardPersonaContext for permission-driven persona resolution
// until the real API (DB-BE-2) is implemented.

const MOCK_PERMISSIONS_BY_ROLE: Record<PlatformRole, ReportPermissions> = {
  administrator: {
    executiveDashboard:    true,
    pmDashboard:           true,
    projectTeamDashboard:  true,
    plReport:              true,
    wipReport:             true,
    utilisationReport:     true,
    budgetVarianceReport:  true,
    auditLogReport:        true,
    projectTransactionsReport: true,
    projectStatusReport:       true,
    projectForecastReport:     true,
    profitabilityReport:       true,
    cashFlowForecastReport:    true,
  },
  executive: {
    executiveDashboard:    true,
    pmDashboard:           false,
    projectTeamDashboard:  false,
    plReport:              true,
    wipReport:             true,
    utilisationReport:     true,
    budgetVarianceReport:  false,
    auditLogReport:        false,
    projectTransactionsReport: true,
    projectStatusReport:       true,
    projectForecastReport:     true,
    profitabilityReport:       true,
    cashFlowForecastReport:    true,
  },
  project_manager: {
    executiveDashboard:    false,
    pmDashboard:           true,
    projectTeamDashboard:  false,
    plReport:              false,
    wipReport:             true,
    utilisationReport:     false,
    budgetVarianceReport:  true,
    auditLogReport:        false,
    projectTransactionsReport: true,
    projectStatusReport:       true,
    projectForecastReport:     true,
    profitabilityReport:       false,
    cashFlowForecastReport:    true,
  },
  project_team: {
    executiveDashboard:    false,
    pmDashboard:           false,
    projectTeamDashboard:  true,
    plReport:              false,
    wipReport:             false,
    utilisationReport:     false,
    budgetVarianceReport:  false,
    auditLogReport:        false,
    projectTransactionsReport: false,
    projectStatusReport:       false,
    projectForecastReport:     false,
    profitabilityReport:       false,
    cashFlowForecastReport:    false,
  },
};

// ── Hook ──────────────────────────────────────────────────────────────────────

const VALID_ROLES: PlatformRole[] = [
  'administrator',
  'executive',
  'project_manager',
  'project_team',
];

function readDebugRole(): PlatformRole | null {
  try {
    const stored = localStorage.getItem('evo_debug_role');
    if (stored && VALID_ROLES.includes(stored as PlatformRole)) {
      return stored as PlatformRole;
    }
  } catch {
    // localStorage unavailable (SSR / private mode)
  }
  return null;
}

/**
 * Returns the current user's platform role.
 *
 * In live mode, fetches from GET /api/users/me/role.
 * Falls back to mock data in demo/tour mode or on API error.
 */
export function useCurrentUserRole(): CurrentUserRole {
  const [apiResult, setApiResult] = useState<CurrentUserRole | null>(null);

  useEffect(() => {
    const mode = getDataMode();
    if (mode === 'demo' || mode === 'tour') return;

    fetchCurrentUserRole()
      .then(({ role, permissions }) => {
        setApiResult({
          role: role as PlatformRole,
          permissions: (permissions as RolePermissions)?.reports ?? null,
          isMock: false,
        });
      })
      .catch((err) => {
        console.error('Failed to fetch user role, using mock:', err);
      });
  }, []);

  return useMemo(() => {
    if (apiResult) return apiResult;

    const debugRole = readDebugRole();
    const role = debugRole ?? 'administrator';
    return {
      role,
      permissions: MOCK_PERMISSIONS_BY_ROLE[role],
      isMock: true,
    };
  }, [apiResult]);
}

/**
 * Non-hook sync version for use in providers that initialise before React renders.
 * Cannot call APIs — returns mock/debug data only. Used for initial render
 * before the async useCurrentUserRole() hook resolves from the API.
 */
export function getCurrentUserRole(): CurrentUserRole {
  const role = readDebugRole() ?? 'administrator';
  return {
    role,
    permissions: MOCK_PERMISSIONS_BY_ROLE[role],
    isMock: true,
  };
}
